import { Vector3 } from "three";
import { CancellationToken } from "../../../../../../../composition/CancellationToken";
import { TweenUtils } from "../../../../../../tween/TweenUtils";
import { GameResourcesPackage } from "../../../../resources/GameResourcesPackage";
import { AudioController } from "../../../AudioController";
import { IProjectileContainer } from "../IProjectileContainer";
import { BaseMole } from "./BaseMole";
import { AttackState } from "./states/AttackState";
import { DieState } from "./states/DieState";
import { FreezeState } from "./states/FreezeState";
import { IdleTimeoutState } from "./states/IdleTimeoutState";
import { LandingState } from "./states/LandingState";
import { IdleToRepeatState } from "./states/IdleToRepeatState";
import { Character } from "../../character/Character";
import { HatType } from "../../../../models/data/HatType";
import { BatchedRenderer } from "three.quarks";
import { GameController } from "../../../../controllers/GameController";

export class PortalMole extends BaseMole {
    private flyCancellation: CancellationToken;
    private _isFlyEnded: boolean = false;
    public get isFlyEnded(): boolean {
        return this._isFlyEnded;
    }

    public constructor(
        gameResources: GameResourcesPackage,
        projectileContainer: IProjectileContainer,
        particlesRenderer: BatchedRenderer,
        character: Character,
        audioController: AudioController,
        gameController: GameController,
        fieldIndex: number
    ) {
        super(
            fieldIndex,
            gameResources,
            projectileContainer,
            particlesRenderer,
            character,
            audioController,
            gameController,
            100,
            Math.random() < 0.5 ? 1500 : 2000
        );

        const dieState = new DieState(this, this.animationController);
        const freezeState = new FreezeState(gameResources, particlesRenderer, this, this.animationController, dieState);
        const idleToRepeatState = new IdleToRepeatState(this, this.animationController, dieState, freezeState, 3000);
        const attackState = new AttackState(this, this.animationController, idleToRepeatState, dieState, freezeState, 3);
        const idleBeforeAttackState = new IdleTimeoutState(this, this.animationController, attackState, dieState, freezeState, 3000);
        const landingState = new LandingState(this, this.animationController, idleBeforeAttackState, dieState, freezeState);
        this.switchState(landingState);
    }

    public override destroy(): void {
        this.flyCancellation?.cancel();
        super.destroy();
    }

    public async sendFlying(startPosition: Vector3, endPosition: Vector3, initialRotation: number): Promise<void> {
        this.flyCancellation = new CancellationToken();
        this.position.copy(startPosition);
        this.moleObject.rotation.y = initialRotation;
        const arcHeight = 5;
        const positionTween = TweenUtils.tweenTo(this.position, endPosition, 720, this.flyCancellation,
            (_: Vector3, elapsed: number) => {
                // Linear interpolate XZ
                this.position.lerpVectors(startPosition, endPosition, elapsed);

                // Add parabolic Y
                const parabola = 4 * elapsed * (1 - elapsed); // 0→1 with peak at 0.5
                this.position.y = startPosition.y + (endPosition.y - startPosition.y) * elapsed + parabola * arcHeight;
            },
        );
        const rotationTween = TweenUtils.tweenTo(this.moleObject.rotation, { x: 0, y: Math.PI, z: 0 }, 720, this.flyCancellation);
        await Promise.all([positionTween, rotationTween]);
        this.updateMatrixWorld(true);
        const charPositionW = this.character.getWorldPosition(new Vector3());
        this.lookAt(charPositionW);
        this.rotateY(Math.PI);
        this.flyCancellation = null;
        this._isFlyEnded = true;
    }

    protected override resolveHatType(): HatType {
        if (this.attackDelay < 2000) {
            return HatType.Pirate;
        }
        return HatType.Cylinder;
    }
}